/**
 * MH Product Hero Gallery Web Component
 * Handles:
 * - PhotoSwipe lightbox initialization with thumbnail navigation
 * - Header-aware sticky sidebar offset
 */

import PhotoSwipeLightbox from './photoswipe-lightbox.esm.min.js';

if (!customElements.get('mh-product-hero-gallery')) {
  customElements.define(
    'mh-product-hero-gallery',
    class MHProductHeroGallery extends HTMLElement {
      constructor() {
        super();
        this.lightbox = null;
        this.headerObserver = null;
        this.sidebar = null;
      }

      connectedCallback() {
        this.sidebar = this.querySelector('.mh-product-hero-gallery__sidebar');

        this.initPhotoSwipe();
        this.initStickyBehavior();
      }

      disconnectedCallback() {
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }

        if (this.headerObserver) {
          this.headerObserver.disconnect();
          this.headerObserver = null;
        }

        if (this._stickyCleanup) {
          window.removeEventListener('scroll', this._stickyCleanup.onScroll);
          window.removeEventListener('resize', this._stickyCleanup.onResize);
          this._stickyCleanup.mql.removeEventListener('change', this._stickyCleanup.onMql);
          this._stickyCleanup = null;
        }
      }

      /**
       * Initialize PhotoSwipe lightbox with custom thumbnail navigation
       */
      initPhotoSwipe() {
        const gallerySelector = this.querySelector('.mh-product-hero-gallery__gallery');
        if (!gallerySelector) return;

        // Check if lightbox is enabled via data attribute
        if (this.dataset.lightbox === 'false') return;

        this.lightbox = new PhotoSwipeLightbox({
          gallery: gallerySelector,
          children: 'a[data-pswp-image]',
          pswpModule: () => import('./photoswipe.esm.min.js'),
          bgOpacity: 0.92,
          showHideAnimationType: 'zoom',
          padding: { top: 40, bottom: 100, left: 20, right: 20 },
        });

        // Add thumbnail strip UI
        this.lightbox.on('uiRegister', () => {
          this.lightbox.pswp.ui.registerElement({
            name: 'thumbnails',
            appendTo: 'wrapper',
            onInit: (el, pswp) => {
              this.createThumbnailStrip(el, pswp);
            },
          });
        });

        // Surface lightbox activity as DOM events so engagement analytics can
        // listen without holding a PhotoSwipe reference.
        this.lightbox.on('beforeOpen', () => {
          this.dispatchLightboxEvent('mh:lightbox-open');
        });
        this.lightbox.on('close', () => {
          this.dispatchLightboxEvent('mh:lightbox-close');
        });

        // Update active thumbnail on slide change
        this.lightbox.on('change', () => {
          this.updateActiveThumbnail();
          this.dispatchLightboxEvent('mh:lightbox-change');
        });

        this.lightbox.init();
      }

      /**
       * Dispatch a bubbling DOM event carrying the current lightbox slide index
       */
      dispatchLightboxEvent(type) {
        const pswp = this.lightbox?.pswp;
        const index = pswp && Number.isFinite(pswp.currIndex) ? pswp.currIndex : 0;
        this.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: { index } }));
      }

      /**
       * Create thumbnail strip at bottom of lightbox
       */
      createThumbnailStrip(container, pswp) {
        container.classList.add('pswp__thumbs');
        container.style.cssText = `
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          gap: 8px;
          padding: 12px;
          background: transparent;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        `;

        const links = this.querySelectorAll('.mh-product-hero-gallery__gallery a[data-pswp-image]');
        links.forEach((link, index) => {
          const thumb = document.createElement('img');
          thumb.classList.add('pswp__thumb');
          thumb.src = link.querySelector('img')?.src || link.href;
          thumb.alt = '';
          thumb.dataset.index = index;
          thumb.style.cssText = `
            width: 60px;
            height: 60px;
            object-fit: cover;
            border-radius: 4px;
            opacity: 0.6;
            cursor: pointer;
            transition: opacity 0.2s ease;
            flex-shrink: 0;
          `;

          if (index === pswp.currIndex) {
            thumb.classList.add('pswp__thumb--active');
            thumb.style.opacity = '1';
          }

          thumb.addEventListener('click', () => {
            pswp.goTo(index);
          });

          container.appendChild(thumb);
        });

        this.thumbsContainer = container;
      }

      /**
       * Update active state on thumbnails
       */
      updateActiveThumbnail() {
        if (!this.thumbsContainer || !this.lightbox?.pswp) return;

        const thumbs = this.thumbsContainer.querySelectorAll('.pswp__thumb');
        const activeIndex = this.lightbox.pswp.currIndex;

        thumbs.forEach((thumb, index) => {
          if (index === activeIndex) {
            thumb.classList.add('pswp__thumb--active');
            thumb.style.opacity = '1';
            // Scroll thumb into view (manual scroll to avoid shifting page)
            const scrollLeft = thumb.offsetLeft - this.thumbsContainer.offsetWidth / 2 + thumb.offsetWidth / 2;
            this.thumbsContainer.scrollTo({ left: scrollLeft, behavior: 'smooth' });
          } else {
            thumb.classList.remove('pswp__thumb--active');
            thumb.style.opacity = '0.6';
          }
        });
      }

      /**
       * Bi-directional sticky sidebar
       *
       * When the sidebar fits in the viewport it sticks at the top like
       * a normal sticky element. When it's taller than the viewport:
       *
       *   Scrolling DOWN → sidebar scrolls with page until its bottom
       *                     reaches the viewport bottom, then sticks.
       *   Scrolling UP   → sidebar scrolls with page until its top
       *                     reaches the viewport top (+ header), then sticks.
       *
       * Implemented by keeping `position: sticky` and updating the `top`
       * property on every scroll frame, clamped between the two extremes.
       */
      initStickyBehavior() {
        if (!this.sidebar) return;

        const header = document.querySelector('.wt-header');
        const mql = window.matchMedia('(min-width: 768px)');
        let lastScrollY = window.scrollY;
        let currentTop = 16;
        let ticking = false;

        const getHeaderOffset = () => {
          if (!header) return 16;
          const headerRect = header.getBoundingClientRect();
          const isSticky =
            header.classList.contains('sticky-enabled') ||
            document.body.classList.contains('page-header-sticky');
          if (isSticky && headerRect.bottom > 0) {
            return headerRect.height + 16;
          }
          return 16;
        };

        const update = () => {
          if (!mql.matches) return;

          const scrollY = window.scrollY;
          const delta = scrollY - lastScrollY;
          lastScrollY = scrollY;

          const sidebarHeight = this.sidebar.offsetHeight;
          const viewportHeight = window.innerHeight;
          const topOffset = getHeaderOffset();
          const bottomGap = 16;

          if (sidebarHeight + topOffset + bottomGap <= viewportHeight) {
            // Sidebar fits in viewport — simple top-stick
            currentTop = topOffset;
          } else {
            // Bi-directional: clamp between bottom-stick and top-stick
            const maxTop = topOffset;
            const minTop = viewportHeight - sidebarHeight - bottomGap;
            currentTop = Math.max(minTop, Math.min(maxTop, currentTop - delta));
          }

          this.sidebar.style.top = `${currentTop}px`;
        };

        const onScroll = () => {
          if (!ticking) {
            requestAnimationFrame(() => {
              update();
              ticking = false;
            });
            ticking = true;
          }
        };

        const onResize = () => {
          if (mql.matches) update();
        };

        const onMql = (e) => {
          if (!e.matches) {
            this.sidebar.style.top = '';
            currentTop = getHeaderOffset();
          } else {
            lastScrollY = window.scrollY;
            currentTop = getHeaderOffset();
            update();
          }
        };

        // Seed initial value
        currentTop = getHeaderOffset();
        if (mql.matches) {
          this.sidebar.style.top = `${currentTop}px`;
        }

        // Watch header sentinel for sticky transitions
        const headerSentinel = document.querySelector('.sticky-header__threshold');
        if (headerSentinel) {
          this.headerObserver = new IntersectionObserver(
            () => { if (mql.matches) update(); },
            { threshold: [0, 1] }
          );
          this.headerObserver.observe(headerSentinel);
        }

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize, { passive: true });
        mql.addEventListener('change', onMql);

        this._stickyCleanup = { onScroll, onResize, onMql, mql };
      }
    }
  );
}
